// Product capability registry: which data product each country serves, for which area types,
// with which measures, availability tables and id format. It is product catalogue 1.0 (city_os#83) as
// the data API serves it (telcofy-web-backend app-backend/lib/catalogue.ts); products.contract.test.ts
// pins it to a copy of that file. No React, router or Firebase imports.
import { COUNTRIES, COUNTRY_CODES, areaCountry } from './countries';
import type { CountryCode } from './countries';
import type { CatalogId } from './catalog';
import { isLegacyMapArea } from './catalogue';

export type DataProductId = Extract<CatalogId, 'activities' | 'odm' | 'flow'>;
/** Every data product, in the order the home page lists them. */
export const DATA_PRODUCT_IDS: readonly DataProductId[] = ['activities', 'odm', 'flow'];
/** Dataset names /data-availability filters on (Firestore product ids). */
export type AvailabilityDataset = 'activities' | 'odm' | 'flows';
export type Granularity = 'hourly' | 'daily';
/**
 * Area types of the catalogue 1.0 maps (config/catalogue.ts): the admin levels by depth in the country's own ladder,
 * and the one model grid (saved as size-tagged cell keys, read one cell size per request).
 */
export type AreaGeoType = 'model_grid' | `admin_lvl_${number}`;
/** How geo ids must reach /data-agg: 'int' for INT64 routine parameters, 'string' for STRING. */
export type IdType = 'int' | 'string';
export type ActivityMeasure =
  | 'sum_unique_people'
  | 'arrivals'
  | 'average_distance'
  | 'average_travel_time'
  | 'average_visit_length'
  | 'stays_count'
  | 'stays_dwell_minutes';
export type MeasureKey = ActivityMeasure | 'trips' | 'people';
export type DataSourceId = 'modelled' | 'unconfirmed';

// ---------------------------------------------------------------------------------------------
// Measures
// ---------------------------------------------------------------------------------------------

export interface MeasureDef {
  key: MeasureKey;
  label: string;
  unit: string;
  definition: string;
  /** May time buckets be summed into a longer period? */
  additiveOverTime: boolean;
  /** May areas (or road links) be summed into a larger one? */
  additiveOverAreas: boolean;
}

// The stays_* flags are deliberately conservative: hourly stays_count counts stay-hours (one stay
// spanning three hours appears in three buckets), and how hourly dwell minutes split a stay has not
// been checked. Flip a flag only after checking the delivery.
export const MEASURES: Readonly<Record<MeasureKey, MeasureDef>> = {
  sum_unique_people: {
    key: 'sum_unique_people',
    label: 'Unique people',
    unit: 'people',
    definition: 'People present in the area during the time bucket, each counted once per area and bucket.',
    additiveOverTime: false,
    additiveOverAreas: false,
  },
  stays_count: {
    key: 'stays_count',
    label: 'Stays',
    unit: 'stays',
    definition: 'Stays (periods when a person stays in one place) in the area during the time bucket. Hourly figures count a long stay in every hour it covers.',
    additiveOverTime: false,
    additiveOverAreas: true,
  },
  stays_dwell_minutes: {
    key: 'stays_dwell_minutes',
    label: 'Minutes spent',
    unit: 'minutes',
    definition: 'Total minutes people spent staying in the area during the time bucket.',
    additiveOverTime: false,
    additiveOverAreas: true,
  },
  arrivals: {
    key: 'arrivals',
    label: 'Arrivals',
    unit: 'arrivals',
    definition: 'Arrivals in the area during the time bucket. Added up exactly over areas; the split by mode is modelled.',
    additiveOverTime: false,
    additiveOverAreas: true,
  },
  average_distance: {
    key: 'average_distance',
    label: 'Average distance',
    unit: 'km',
    definition: 'Average distance travelled to the area.',
    additiveOverTime: false,
    additiveOverAreas: false,
  },
  average_travel_time: {
    key: 'average_travel_time',
    label: 'Average travel time',
    unit: 'min',
    definition: 'Average travel time to the area.',
    additiveOverTime: false,
    additiveOverAreas: false,
  },
  average_visit_length: {
    key: 'average_visit_length',
    label: 'Average visit length',
    unit: 'min',
    definition: 'Average length of a visit to the area.',
    additiveOverTime: false,
    additiveOverAreas: false,
  },
  trips: {
    key: 'trips',
    label: 'Trips',
    unit: 'trips',
    definition: 'Estimated trips from the origin area to the destination area in the time bucket.',
    additiveOverTime: true,
    additiveOverAreas: true,
  },
  people: {
    key: 'people',
    label: 'People (in cars)',
    unit: 'people',
    definition: 'People in cars passing along the road during the hour, per direction.',
    additiveOverTime: true,
    additiveOverAreas: false,
  },
};

const ACTIVITY_MEASURE_KEYS: readonly MeasureKey[] = [
  'sum_unique_people',
  'arrivals',
  'average_distance',
  'average_travel_time',
  'average_visit_length',
  'stays_count',
  'stays_dwell_minutes',
];

/** True for the measures the Activities request accepts (the others belong to ODM and Flow). */
export function isActivityMeasure(m: MeasureKey): m is ActivityMeasure {
  return ACTIVITY_MEASURE_KEYS.includes(m);
}

/** Backend-valid but not offered in the UI. Removing a measure from this list (and adding it to a geo's measures) brings it back. */
export const HIDDEN_MEASURES: readonly MeasureKey[] = [];

// ---------------------------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------------------------

export interface Blocker {
  code: 'needs-maps';
  reason: string;
}

/**
 * The target is selectable and sent to the API, but the backend cannot serve it yet. The UI marks
 * it as draft data; a request that fails or returns no rows says why. Unlike `blocked`, it never
 * stops a request.
 */
export interface Pending {
  code: 'backend-pending';
  reason: string;
}

export interface GeoCapability {
  geoType: AreaGeoType;
  /** How ids must reach /data-agg. */
  idType: IdType;
  /** Measures the UI offers; [0] is the default. */
  measures: readonly MeasureKey[];
  /** Availability table_name per grain. A missing grain is not served. */
  tables: Readonly<Partial<Record<Granularity, string>>>;
  /** The backend accepts this target but the app cannot use it yet; shown with the reason, Submit refused. */
  blocked?: Blocker;
  /** Selectable, but the backend fix it depends on is not live yet (draft data). */
  pending?: Pending;
}

interface CapabilityBase {
  country: CountryCode;
  dataset: AvailabilityDataset;
  source: DataSourceId;
  defaultGranularity: Granularity;
  /**
   * Consecutive batches overlap, so a window can return the same hour from two batches and results
   * must be de-duplicated. The old LAT/LTU deliveries did (28 hourly buckets per batch); catalogue 1.0
   * holds one batch per day (a row's day is its batch_date), so no country does now.
   */
  dedupeOverlappingBatches: boolean;
  /** Longest run of days one request may cover; absent means one day. Turn on for multi-day Flow (#follow-up). */
  maxRangeDays?: number;
  /** Plain-English caveats that hold for this country and product only; shown in "About this data". */
  notes?: readonly string[];
}

export interface AreaCapability extends CapabilityBase {
  product: 'activities';
  geo: readonly GeoCapability[];
}

export interface OdCapability extends CapabilityBase {
  product: 'odm';
  /** Origin and destination may each be any listed geo type. */
  geo: readonly GeoCapability[];
  /** The backend only uses it to name the export folder. */
  requestMeasure: 'trips';
  /** Optional row fields this delivery models; the rest come back null. */
  optionalFields: readonly ('General_Distance' | 'Average_Speed')[];
}

export interface PointCapability extends CapabilityBase {
  product: 'flow';
  measure: 'people';
  /** Flow reads the hourly table (catalogue 1.0 also has flows_daily, which no page reads yet). */
  tables: Readonly<{ hourly: string }>;
  /** The Next dev backend has no GET flow preview, so results come from POST plus polling. */
  syncPreview: false;
}

export type ProductCapability = AreaCapability | OdCapability | PointCapability;
export type CapabilityOf<P extends DataProductId> = Extract<ProductCapability, { product: P }>;

type CountryCapabilities = { readonly [P in DataProductId]?: CapabilityOf<P> };

export const PRODUCT_LABELS: Readonly<Record<DataProductId, string>> = {
  activities: 'Activities',
  odm: 'Origin–Destination',
  flow: 'Flow',
};

/** How a time resolution is written for people: the picker, the summaries and the previews. */
export const GRANULARITY_LABELS: Readonly<Record<Granularity, string>> = { daily: 'Daily', hourly: 'Hourly' };

/**
 * Delivery days of the real Estonian deliveries as [year, month, day], used only to anchor draft
 * mock availability when the availability endpoint lists none. Kept as numbers because no source
 * file may write a delivery date as text (src/__tests__/noHardcodedDates.test.ts).
 */
export const DRAFT_FALLBACK_DELIVERY: Readonly<Partial<Record<CountryCode, readonly (readonly [number, number, number])[]>>> = {
  EST: [
    [2025, 10, 11],
    [2025, 10, 14],
  ],
};

// ---------------------------------------------------------------------------------------------
// Catalogue 1.0 (city_os#83)
// ---------------------------------------------------------------------------------------------
//
// Admin areas are full totals in activity_admin_* / odm_admin_*. The model grid is one adaptive grid (250 m to 10 km)
// whose products are nested totals in activity_adaptive_* / odm_adaptive_*: a shown cell includes the cells under it.
// Every id is a string: admin ids carry the country and level (NO_2_0301), grid ids are SSB squares in Norway and
// INSPIRE ids elsewhere. The data API takes one geo type per request, and grid ODM pairs cells of one size only.

/** The admin levels each country serves (backend catalogue.ts COUNTRIES.admin, less `country`): the depth in its own ladder. */
export const CATALOGUE_ADMIN_LEVELS: Readonly<Record<CountryCode, readonly number[]>> = {
  NOR: [1, 2, 3, 4],
  EST: [1, 2, 3],
  LAT: [1],
  LTU: [2],
};

const ADMIN_TABLES = (p: 'activity' | 'odm') => ({ hourly: `${p}_admin_hourly`, daily: `${p}_admin_daily` }) as const;
const ADAPTIVE_TABLES = (p: 'activity' | 'odm') => ({ hourly: `${p}_adaptive_hourly`, daily: `${p}_adaptive_daily` }) as const;
const FLOW_TABLES = { hourly: 'flows_hourly' } as const;

const ACTIVITY_ADMIN_MEASURES: readonly MeasureKey[] = [
  'sum_unique_people',
  'arrivals',
  'average_distance',
  'average_travel_time',
  'average_visit_length',
];
const ACTIVITY_ADAPTIVE_MEASURES: readonly MeasureKey[] = [...ACTIVITY_ADMIN_MEASURES, 'stays_count', 'stays_dwell_minutes'];

const NOTES: Readonly<Record<DataProductId, readonly string[]>> = {
  activities: [
    'Fewer than 5 people in a place are not published, so those values are missing, not zero.',
    'A figure this country has no source for (such as a travel mode it does not model) is shown as not available, never as zero.',
  ],
  odm: [
    'Fewer than 5 trips between two places are not published, so those pairs are missing, not zero.',
    'Grid cells are read at one cell size: the adaptive data only pairs cells of the same size.',
  ],
  flow: ['Fewer than 5 people on a road in an hour are not published, so those hours are missing, not zero.'],
};

function geosOf(c: CountryCode, p: 'activity' | 'odm'): GeoCapability[] {
  const measures = p === 'activity' ? ACTIVITY_ADMIN_MEASURES : (['trips'] as const);
  return [
    ...CATALOGUE_ADMIN_LEVELS[c].map(
      (n): GeoCapability => ({ geoType: `admin_lvl_${n}`, idType: 'string', measures, tables: ADMIN_TABLES(p) }),
    ),
    {
      geoType: 'model_grid',
      idType: 'string',
      measures: p === 'activity' ? ACTIVITY_ADAPTIVE_MEASURES : measures,
      tables: ADAPTIVE_TABLES(p),
    },
  ];
}

function countryCapabilities(c: CountryCode, source: DataSourceId): CountryCapabilities {
  const base = { country: c, source, dedupeOverlappingBatches: false };
  return {
    activities: {
      ...base,
      product: 'activities',
      dataset: 'activities',
      defaultGranularity: 'daily',
      notes: NOTES.activities,
      geo: geosOf(c, 'activity'),
    },
    odm: {
      ...base,
      product: 'odm',
      dataset: 'odm',
      defaultGranularity: 'daily',
      notes: NOTES.odm,
      geo: geosOf(c, 'odm'),
      requestMeasure: 'trips',
      optionalFields: ['General_Distance', 'Average_Speed'],
    },
    flow: {
      ...base,
      product: 'flow',
      dataset: 'flows',
      defaultGranularity: 'hourly',
      notes: NOTES.flow,
      measure: 'people',
      tables: FLOW_TABLES,
      syncPreview: false,
    },
  };
}

export const CAPABILITIES: { readonly [C in CountryCode]: CountryCapabilities } = {
  NOR: countryCapabilities('NOR', 'modelled'),
  EST: countryCapabilities('EST', 'modelled'),
  LAT: countryCapabilities('LAT', 'unconfirmed'),
  LTU: countryCapabilities('LTU', 'unconfirmed'),
};

export function capability<P extends DataProductId>(c: CountryCode, p: P): CapabilityOf<P> | undefined {
  return CAPABILITIES[c][p] as CapabilityOf<P> | undefined;
}

export function geoCapability(
  c: CountryCode | null,
  p: 'activities' | 'odm',
  geoType: string | null,
): GeoCapability | undefined {
  if (c == null || geoType == null) return undefined;
  return capability(c, p)?.geo.find((g) => g.geoType === geoType);
}

/**
 * The Activities measures a person can pick for a country and area type: what the registry lists,
 * less any hidden measure, in registry order. Empty when the country or area type has no capability.
 */
export function offeredActivityMeasures(c: CountryCode | null, geoType: string | null): ActivityMeasure[] {
  return (geoCapability(c, 'activities', geoType)?.measures ?? [])
    .filter(isActivityMeasure)
    .filter((m) => !HIDDEN_MEASURES.includes(m));
}

function usableGeo(cap: AreaCapability | OdCapability): GeoCapability[] {
  return cap.geo.filter((g) => !g.blocked);
}

function hasUsableTarget(c: CountryCode, p: DataProductId): boolean {
  const cap = capability(c, p);
  if (!cap) return false;
  return cap.product === 'flow' ? true : usableGeo(cap).length > 0;
}

/** Countries with at least one non-blocked target for the product, in registry order. */
export function countriesWith(p: DataProductId): CountryCode[] {
  return COUNTRY_CODES.filter((c) => hasUsableTarget(c, p));
}

export type CountrySupport =
  | { ok: true }
  | { ok: false; code: 'no-product' | 'blocked'; reason: string; alternatives: CountryCode[] };

/** Can `p` be used at all in `c`? Country level: true when hasUsableTarget(c, p). */
export function countrySupport(c: CountryCode, p: DataProductId): CountrySupport {
  if (hasUsableTarget(c, p)) return { ok: true };
  const alternatives = countriesWith(p).filter((x) => x !== c);
  const cap = capability(c, p);
  if (!cap) {
    return { ok: false, code: 'no-product', reason: `Not offered in ${COUNTRIES[c].label} yet.`, alternatives };
  }
  const blocked = cap.product === 'flow' ? undefined : cap.geo.find((g) => g.blocked)?.blocked;
  return {
    ok: false,
    code: 'blocked',
    reason: blocked?.reason ?? `Not offered in ${COUNTRIES[c].label} yet.`,
    alternatives,
  };
}

/** The products the public demo API serves for DEMO_API_COUNTRY (services/demoApi.ts). */
export const DEMO_API_PRODUCTS: readonly DataProductId[] = ['activities', 'odm'];

/**
 * GET /users/:uid/products key that unlocks each data product (Trello #410). Key names are
 * provisional: Alexey is finalising them at the CRM level, so keep them here in one place. Test
 * users get every key back from the endpoint (backend `isTest` short-circuit).
 */
export const DATA_PRODUCT_ENTITLEMENT_KEYS: Readonly<Record<DataProductId, string>> = {
  activities: 'activity',
  odm: 'odm',
  flow: 'flow',
};

// ---------------------------------------------------------------------------------------------
// Support status
// ---------------------------------------------------------------------------------------------

export type Support =
  | { ok: true; capability: ProductCapability; geo?: GeoCapability; pending?: Pending }
  | {
      ok: false;
      code: 'no-country' | 'no-product' | 'no-geo-type' | 'blocked';
      reason: string;
      alternatives: CountryCode[];
    };

const countryNames = (codes: readonly CountryCode[]) => codes.map((c) => COUNTRIES[c].label).join(', ');

/**
 * Can `p` be requested for areas of `geoType` in country `c`? Checks, in order: a country is
 * known, the country has the product, (activities/odm) the geo type is listed, and it is not
 * blocked. Flow ignores geoType. `alternatives` lists other countries where the product works.
 */
export function supportStatus(c: CountryCode | null, p: DataProductId, geoType: string | null): Support {
  const product = PRODUCT_LABELS[p];
  const elsewhere = countriesWith(p).filter((x) => x !== c);
  const refuse = (code: Exclude<Support, { ok: true }>['code'], reason: string): Support => ({
    ok: false,
    code,
    reason,
    alternatives: elsewhere,
  });

  if (c == null) {
    // Activities and Flow open from a saved area. Origin-Destination opens from its own setup page.
    return refuse(
      'no-country',
      p === 'odm'
        ? `Select areas on the ${PRODUCT_LABELS.odm} setup page first.`
        : 'Open this page from a saved area.',
    );
  }

  const country = COUNTRIES[c].label;
  const cap = capability(c, p);
  if (!cap) {
    const list = elsewhere.length ? `Available for: ${countryNames(elsewhere)}.` : 'It is not available in any country yet.';
    return refuse('no-product', `${product} isn't available for ${country}. ${list}`);
  }
  if (cap.product === 'flow') return { ok: true, capability: cap };

  const geo = cap.geo.find((g) => g.geoType === geoType);
  if (!geo) {
    const usable = usableGeo(cap);
    const next = usable.length
      ? `Use: ${usable.map((g) => geoTypeLabel(g.geoType, c)).join(', ')}.`
      : cap.geo.find((g) => g.blocked)?.blocked?.reason ?? '';
    const head =
      geoType == null
        ? `Choose an area type for ${product} in ${country}.`
        : `${product} isn't available for ${geoTypeLabel(geoType, c)} in ${country}.`;
    return refuse('no-geo-type', next ? `${head} ${next}` : head);
  }
  if (geo.blocked) return refuse('blocked', geo.blocked.reason);
  return { ok: true, capability: cap, geo, ...(geo.pending ? { pending: geo.pending } : {}) };
}

/** The pending status of a target (draft data), if it has one. Pending targets are still usable. */
export function pendingFor(
  c: CountryCode | null | undefined,
  p: 'activities' | 'odm',
  geoType: string | null | undefined,
): Pending | undefined {
  return geoCapability(c ?? null, p, geoType ?? null)?.pending;
}

/**
 * supportStatus for a saved area. An area with no usable country is refused with copy that suits a
 * list ("This area has no country set, ..."), and the Library groups it under "No country set".
 */
export function areaSupport(
  area: { country_code?: string | null; type: string; ids?: unknown; geometry?: unknown },
  p: DataProductId,
): Support {
  const country = areaCountry(area);
  if (country == null) {
    return {
      ok: false,
      code: 'no-country',
      reason: `This area has no country set, so ${PRODUCT_LABELS[p]} can't use it.`,
      alternatives: countriesWith(p),
    };
  }
  // An area made on the old maps names areas catalogue 1.0 data does not read, whatever its type is called.
  if (p !== 'flow' && isLegacyMapArea(area)) {
    return {
      ok: false,
      code: 'no-geo-type',
      reason: `This area was made on the old maps, which ${PRODUCT_LABELS[p]} no longer reads.`,
      alternatives: countriesWith(p).filter((x) => x !== country),
    };
  }
  return supportStatus(country, p, area.type);
}

/** Area types Real Time can monitor. */
export const REALTIME_GEO_TYPES: readonly string[] = ['custom_polygon'];

/**
 * Real Time: custom polygons in countries with `realtime`. Polygons saved before areas had a
 * country stay usable (Real Time has only served Norway).
 */
export function realtimeSupports(area: { country_code?: string | null; type: string }): boolean {
  if (!REALTIME_GEO_TYPES.includes(area.type)) return false;
  const country = areaCountry(area);
  return country == null || COUNTRIES[country].realtime;
}

/** Availability table_name for a target and grain; undefined when that grain is not served. */
export function availabilityTable(
  c: CountryCode,
  p: DataProductId,
  geoType: string | null,
  g: Granularity,
): string | undefined {
  if (p === 'flow') {
    const tables: Readonly<Partial<Record<Granularity, string>>> | undefined = capability(c, 'flow')?.tables;
    return tables?.[g];
  }
  return geoCapability(c, p, geoType)?.tables[g];
}

/**
 * Converts selected area ids to what /data-agg expects. 'int' trims and accepts plain digits
 * only (anything else, such as an INSPIRE cell id, is refused rather than sent as NaN/null);
 * 'string' sends String(id).
 */
export function toApiGeoIds(
  idType: IdType,
  ids: readonly (string | number)[],
): { ok: true; ids: (number | string)[] } | { ok: false; reason: string } {
  const out: (number | string)[] = [];
  for (const id of ids) {
    if (idType === 'string') {
      const s = String(id);
      if (s.trim() === '') return { ok: false, reason: 'An area has an empty id, so the request cannot be sent.' };
      out.push(s);
      continue;
    }
    const s = String(id).trim();
    const n = Number(s);
    if (!/^\d+$/.test(s) || !Number.isSafeInteger(n)) {
      return { ok: false, reason: `Area id "${s}" can't be sent: this area type needs numeric ids.` };
    }
    out.push(n);
  }
  return { ok: true, ids: out };
}

// ---------------------------------------------------------------------------------------------
// Labels and copy
// ---------------------------------------------------------------------------------------------

const GEO_TYPE_LABELS: Readonly<Record<string, string>> = {
  admin_level_2: 'Municipalities',
  admin_level_4: 'Basic statistical units',
  grid_1000m: '1 km grid',
  grid_250m: '250 m grid',
  custom_polygon: 'Custom polygons',
  osm_links: 'OSM road links',
  statens_vegvesen: 'Statens vegvesen roads',
  // Catalogue 1.0 (config/catalogue.ts); its admin levels are named per country below.
  model_grid: 'Grid (250 m–10 km)',
  grid_500m: '500 m grid',
  grid_5000m: '5 km grid',
  grid_10000m: '10 km grid',
  network_links: 'Road links',
  station: 'Border crossings',
  country: 'Whole country',
};

const GEO_TYPE_LABELS_BY_COUNTRY: Readonly<Partial<Record<CountryCode, Readonly<Record<string, string>>>>> = {
  NOR: {
    admin_level_2: 'Municipalities (kommuner)',
    admin_level_4: 'Basic statistical units (grunnkretser)',
    // Catalogue 1.0: the depth in Norway's own ladder.
    admin_lvl_1: 'Counties (fylker)',
    admin_lvl_2: 'Municipalities (kommuner)',
    admin_lvl_3: 'Districts (delområder)',
    admin_lvl_4: 'Basic statistical units (grunnkretser)',
  },
  EST: {
    admin_level_2: 'Municipalities',
    admin_level_4: 'Settlements',
    admin_lvl_1: 'Counties (maakonnad)',
    admin_lvl_2: 'Municipalities (omavalitsused)',
    admin_lvl_3: 'Settlements (asustusüksused)',
  },
  LAT: { admin_lvl_1: 'Municipalities (novadi)' },
  LTU: { admin_lvl_2: 'Municipalities (savivaldybės)' },
};

/** Human label for a geo type, country-specific where the admin levels differ. Unknown types return as-is. */
export function geoTypeLabel(geoType: string, c?: CountryCode | null): string {
  const own = c ? GEO_TYPE_LABELS_BY_COUNTRY[c]?.[geoType] : undefined;
  // A catalogue admin level nobody has named yet still reads as one.
  const level = /^admin_lvl_(\d+)$/.exec(geoType);
  return own ?? GEO_TYPE_LABELS[geoType] ?? (level ? `Admin areas, level ${level[1]}` : geoType);
}

// The same area types as something to count: "1 municipality", "3 municipalities". The labels
// above name a whole group ("Municipalities (kommuner)"), so they cannot be counted as they are.
type Noun = readonly [singular: string, plural: string];

const GEO_TYPE_NOUNS: Readonly<Record<string, Noun>> = {
  admin_level_2: ['municipality', 'municipalities'],
  admin_level_4: ['basic statistical unit', 'basic statistical units'],
  grid_1000m: ['grid cell', 'grid cells'],
  grid_250m: ['grid cell', 'grid cells'],
  custom_polygon: ['custom polygon', 'custom polygons'],
  osm_links: ['OSM road link', 'OSM road links'],
  statens_vegvesen: ['road', 'roads'],
  model_grid: ['grid cell', 'grid cells'],
  network_links: ['road link', 'road links'],
  station: ['border crossing', 'border crossings'],
};

const GEO_TYPE_NOUNS_BY_COUNTRY: Readonly<Partial<Record<CountryCode, Readonly<Record<string, Noun>>>>> = {
  EST: {
    admin_level_4: ['settlement', 'settlements'],
    admin_lvl_1: ['county', 'counties'],
    admin_lvl_2: ['municipality', 'municipalities'],
    admin_lvl_3: ['settlement', 'settlements'],
  },
  NOR: {
    admin_lvl_1: ['county', 'counties'],
    admin_lvl_2: ['municipality', 'municipalities'],
    admin_lvl_3: ['district', 'districts'],
    admin_lvl_4: ['basic statistical unit', 'basic statistical units'],
  },
  LAT: { admin_lvl_1: ['municipality', 'municipalities'] },
  LTU: { admin_lvl_2: ['municipality', 'municipalities'] },
};

/**
 * The singular and plural of an area type, country-specific where the admin levels differ, for
 * `pluralize(n, singular, plural)`. An unknown or missing type is just an "area".
 */
export function geoTypeNoun(geoType: string | null | undefined, c?: CountryCode | null): { singular: string; plural: string } {
  const noun = (geoType && ((c && GEO_TYPE_NOUNS_BY_COUNTRY[c]?.[geoType]) || GEO_TYPE_NOUNS[geoType])) || undefined;
  return noun ? { singular: noun[0], plural: noun[1] } : { singular: 'area', plural: 'areas' };
}

/**
 * Product-card gating accepts both spellings until the backend settles product ids:
 * cards use `activity`/`flow`, the availability ACL uses `activities`/`flows`.
 */
export const ENTITLEMENT_ALIASES: Readonly<Record<string, readonly string[]>> = {
  activity: ['activity', 'activities'],
  flow: ['flow', 'flows'],
  odm: ['odm'],
  realtime: ['realtime'],
  emergency: ['emergency'],
};

export interface DataSourceDef {
  /** Short badge text; null = show no badge. */
  badge: string | null;
  /** Plain name used in the About panel and in the CSV "Source" column. */
  name: string;
  summary: string;
  /** Caveats that hold for this source, per product. */
  caveatsByProduct: Readonly<Partial<Record<DataProductId, readonly string[]>>>;
}

export const DATA_SOURCES: Readonly<Record<DataSourceId, DataSourceDef>> = {
  // NOR and EST (data-delivery docs: "the Estonian and Norwegian data are modelled").
  modelled: {
    badge: 'Modelled',
    name: 'Modelled',
    summary:
      'Modelled with an activity-based travel model of residents; not measured from mobile network data.',
    caveatsByProduct: {
      flow: ["Flows count residents' cars only."],
    },
  },
  // LAT and LTU: the source wording is still David's decision, so no badge.
  unconfirmed: {
    badge: null,
    name: 'Not confirmed yet',
    summary: 'The source and method of this delivery are not confirmed yet.',
    caveatsByProduct: {
      odm: ['Origin–destination pairs with fewer than 5 trips are not published, so adding up pairs undercounts trips.'],
    },
  },
};

/** Badge text for a product in a country; null = no badge. Flow from modelled sources adds the residents' cars scope. */
export function sourceBadgeText(c: CountryCode, p: DataProductId): string | null {
  const cap = capability(c, p);
  if (!cap) return null;
  const badge = DATA_SOURCES[cap.source].badge;
  if (badge === null) return null;
  return p === 'flow' ? `${badge} · residents' cars` : badge;
}

/** Plain-English "do not add this up" warnings for a measure, derived from its additivity flags. */
export function measureCaveats(m: MeasureKey): string[] {
  const def = MEASURES[m];
  const l = def.label.toLowerCase();
  // An average is never added up: it is weighted, not a count of people.
  if (m.startsWith('average_')) return [`Do not add up the ${l} across hours or across areas: it is an average.`];
  if (!def.additiveOverTime && !def.additiveOverAreas) {
    return [`Do not add up ${l} across hours or across areas: one person can be counted in several.`];
  }
  if (!def.additiveOverTime) return [`Do not add up ${l} across hours.`];
  if (!def.additiveOverAreas) return [`Do not add up ${l} across areas.`];
  return [];
}

export interface AboutThisData {
  badge: string | null;
  sourceName: string;
  summary: string;
  caveats: string[];
}

/**
 * What the "About this data" panel shows for a product in a country: the source, and every caveat
 * that applies (the source's own, the country's notes and, when a measure is given, its additivity).
 * Null when the country does not serve the product.
 */
export function aboutThisData(c: CountryCode, p: DataProductId, measure?: MeasureKey): AboutThisData | null {
  const cap = capability(c, p);
  if (!cap) return null;
  const src = DATA_SOURCES[cap.source];
  const caveats = [
    ...(src.caveatsByProduct[p] ?? []),
    ...(cap.notes ?? []),
    ...(measure ? measureCaveats(measure) : []),
  ];
  return {
    badge: sourceBadgeText(c, p),
    sourceName: src.name,
    summary: src.summary,
    caveats: caveats.filter((text, i) => caveats.indexOf(text) === i),
  };
}
